import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { FaPlus, FaTrash, FaChevronUp, FaChevronDown, FaSave, FaUndo, FaLock, FaWpforms, FaQrcode } from 'react-icons/fa';
import api from '../axiosSetup';
import Swal from 'sweetalert2';
import CustomNavbar from '../CustomNavbar';
import AdminCostumeForms from './AdminCostumeForms';
import AdminCostumeTypeDefinition from './AdminCostumeTypeDefinition';
import './css/admin.css';
import cfg from '../../config';

// Klartext für die Feldtypen, die das Backend akzeptiert.
// Funktion statt Konstante: auf Modulebene wuerde der Rueckfallwert aus
// config.js eingefroren, bevor AppContext die Serverwerte uebernimmt.
const TYPE_LABELS = () => ({
    'text':           'Text',
    'textarea':       'Mehrzeiliger Text',
    'date':           'Datum',
    'time':           'Uhrzeit',
    'datetime':       'Datum und Uhrzeit',
    'checkbox':       'Ja/Nein',
    'number':         'Zahl',
    'select':         'Auswahlliste',
    'multiselect':    'Mehrfachauswahl',
    // Die Optionen dieses Typs stehen nicht im Formular, sondern in der
    // Verwaltung der Typen. Deshalb gibt es sie hier nicht zu bearbeiten.
    'costume-type-select': `${cfg.costumeTerm}typen (zentral gepflegt)`,
    // Nur ein Platzhalter: er markiert die Stelle, an der die Felder des
    // jeweiligen Typs erscheinen. Gepflegt werden sie bei den Typen.
    'type-questions': `Felder des ${cfg.costumeTerm}typs (dort gepflegt)`,
    'departure-list': 'Abfahrtsorte (Liste)',
});

// Erklärung, wofür das jeweilige Formular zuständig ist
// Funktion statt Konstante: auf Modulebene wuerde der Rueckfallwert aus
// config.js eingefroren, bevor AppContext die Serverwerte uebernimmt.
const FORM_INFO = () => ({
    'ZRMoveEdit.jsx': `Felder beim Anlegen und Bearbeiten: ${cfg.eventTerm}`,
});

const ZEIT_TYPEN = ['time', 'datetime'];
const leeresFeld = () => ({ name: '', label: '', type: 'text', required: false });

const AdminForms = () => {
    const [forms, setForms]               = useState([]);
    const [kostuemForms, setKostuemForms] = useState([]);
    const [typen, setTypen]               = useState([]);
    const [protectedFields, setProtected] = useState({});
    const [selected, setSelected]         = useState(null);
    const [scheme, setScheme]             = useState(null);
    const [original, setOriginal]         = useState(null);
    const [loading, setLoading]           = useState(true);
    const [saving, setSaving]             = useState(false);
    const [error, setError]               = useState(null);

    const [reiter, setReiter]             = useState('formulare');
    const [neuerTyp, setNeuerTyp]         = useState('');
    const [legtAn, setLegtAn]             = useState(false);

    const ladeListe = useCallback(async () => {
        try {
            const { data } = await api.get('/api/admin/forms');
            setForms(data.forms || []);
            setProtected(data.protectedFields || {});
        } catch {
            setError('Die Formularliste konnte nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Die Formulare rund ums Kostuem und die Typen dazu. Sie haengen an
    // eigenen Schnittstellen, gehoeren fuer den Anwender aber in dieselbe
    // Liste wie alle anderen Formulare.
    const ladeKostuem = useCallback(async () => {
        try {
            const [formulare, typListe] = await Promise.all([
                api.get('/api/costume-forms'),
                api.get('/api/costume-types'),
            ]);
            setKostuemForms(formulare.data.formulare || []);
            setTypen(typListe.data || []);
        } catch {
            setError(`Die ${cfg.costumeTerm}typen konnten nicht geladen werden.`);
        }
    }, []);

    useEffect(() => { ladeListe(); ladeKostuem(); }, [ladeListe, ladeKostuem]);

    const istKostuemFormular = (component) =>
        kostuemForms.some(f => f.component === component);

    const oeffne = async (component) => {
        setError(null);
        // Die Kostuem-Formulare bringen ihren eigenen Editor mit: sie
        // kennen gesperrte Felder, Auswahlmoeglichkeiten und Bedingungen.
        if (istKostuemFormular(component)) {
            setSelected(component);
            setScheme(null);
            setOriginal(null);
            return;
        }
        try {
            const { data } = await api.get(`/api/form/${encodeURIComponent(component)}`);
            setSelected(component);
            setScheme(data);
            setOriginal(JSON.stringify(data));
        } catch {
            setError(`Das Formular "${component}" konnte nicht geladen werden.`);
        }
    };

    const legeTypAn = async (e) => {
        e.preventDefault();
        const name = neuerTyp.trim();
        if (!name) return;
        setLegtAn(true);
        setError(null);
        try {
            await api.post('/api/costume-types', { name });
            setNeuerTyp('');
            await ladeKostuem();
        } catch (err) {
            setError(err.response?.data?.message || 'Der Typ konnte nicht angelegt werden.');
        } finally {
            setLegtAn(false);
        }
    };

    const loescheTyp = async (typ) => {
        const { isConfirmed } = await Swal.fire({
            title: `${cfg.costumeTerm}typ löschen?`,
            html: `<b>${typ.name}</b><br/><span style="opacity:.7;font-size:.9em">`
                + 'Die Felder dieses Typs und die Zuweisungen der Verantwortlichen '
                + 'verschwinden mit. Bereits gestellte Anträge bleiben erhalten.</span>',
            icon: 'warning', showCancelButton: true,
            confirmButtonText: 'Löschen', cancelButtonText: 'Abbrechen',
            confirmButtonColor: '#dc3545', background: '#212429', color: '#fff',
        });
        if (!isConfirmed) return;
        try {
            await api.delete(`/api/costume-types/${typ.id}`);
            await ladeKostuem();
        } catch (err) {
            setError(err.response?.data?.message || 'Der Typ konnte nicht gelöscht werden.');
        }
    };

    const geaendert  = scheme && original && JSON.stringify(scheme) !== original;
    const istUmzug   = selected === 'ZRMoveEdit.jsx';
    const gesperrtes = (name) => (protectedFields[selected] || []).includes(name);

    const setzeFeld = (index, key, wert) => {
        const fields = [...scheme.fields];
        fields[index] = { ...fields[index], [key]: wert };
        setScheme({ ...scheme, fields });
    };

    const verschiebe = (index, richtung) => {
        const ziel = index + richtung;
        if (ziel < 0 || ziel >= scheme.fields.length) return;
        const fields = [...scheme.fields];
        [fields[index], fields[ziel]] = [fields[ziel], fields[index]];
        setScheme({ ...scheme, fields });
    };

    const entferne = async (index) => {
        const feld = scheme.fields[index];
        if (gesperrtes(feld.name)) return;
        const { isConfirmed } = await Swal.fire({
            title: 'Feld entfernen?',
            html: `<b>${feld.label || feld.name}</b><br/><span style="opacity:.7;font-size:.9em">Bereits gespeicherte Werte bleiben erhalten, werden aber nicht mehr angezeigt.</span>`,
            icon: 'warning', showCancelButton: true,
            confirmButtonText: 'Entfernen', cancelButtonText: 'Abbrechen',
            confirmButtonColor: '#dc3545', background: '#212429', color: '#fff',
        });
        if (!isConfirmed) return;
        setScheme({ ...scheme, fields: scheme.fields.filter((_, i) => i !== index) });
    };

    const speichere = async () => {
        setSaving(true);
        setError(null);
        try {
            await api.put(`/api/admin/forms/${encodeURIComponent(selected)}`, { scheme });
            setOriginal(JSON.stringify(scheme));
            await ladeListe();
            Swal.fire({
                icon: 'success', title: 'Gespeichert',
                text: 'Das Formular ist sofort aktiv.',
                background: '#212429', color: '#fff', timer: 1800, showConfirmButton: false,
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Speichern fehlgeschlagen.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <><CustomNavbar />
            <div className="admin-page text-center py-5"><Spinner animation="border" variant="warning" /></div>
        </>
    );

    const auswahlKnopf = (component, titel, beschreibung, anzahl, inOrdnung) => (
        <button
            key={component}
            type="button"
            className={`admin-picker__item ${selected === component ? 'is-active' : ''}`}
            onClick={() => oeffne(component)}
        >
            <span className="admin-picker__name">
                {titel}
                <Badge bg={inOrdnung ? 'dark' : 'danger'} className="fw-normal">
                    {inOrdnung ? `${anzahl}` : '!'}
                </Badge>
            </span>
            <span className="admin-picker__desc">{beschreibung}</span>
        </button>
    );

    return (
        <>
            <CustomNavbar />
            <div className="admin-page">

                <header className="admin-head">
                    <div className="admin-head__icon"><FaWpforms /></div>
                    <div>
                        <h1 className="admin-head__title">Formulare gestalten</h1>
                        <p className="admin-head__sub">
                            Lege fest, welche Felder in den Formularen erscheinen — bei
                            {' '}{cfg.eventTermPlural} ebenso wie bei Anträgen und Bestellungen rund um
                            {' '}{cfg.costumeTermPlural}. Änderungen wirken sofort, ohne Neustart
                            und ohne neuen Build.
                        </p>
                        <p className="admin-head__sub">
                            Wer für einen Typ zuständig ist, wird weiterhin unter
                            {' '}<strong>{cfg.adminRoleName} → {cfg.costumeTerm}typen &amp; Manager</strong> zugewiesen.
                        </p>
                    </div>
                </header>

                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

                <ul className="nav nav-pills mb-3 gap-1">
                    <li className="nav-item">
                        <button className={`nav-link ${reiter === 'formulare' ? 'active' : ''}`}
                            onClick={() => setReiter('formulare')}>
                            Formulare
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${reiter === 'typen' ? 'active' : ''}`}
                            onClick={() => setReiter('typen')}>
                            {cfg.costumeTerm}typen <Badge bg="secondary">{typen.length}</Badge>
                        </button>
                    </li>
                </ul>

                {reiter === 'formulare' ? (
                    <Row className="g-3">
                        {/* ── Auswahl ───────────────────────────────────────────── */}
                        <Col lg={4}>
                            <div className="admin-card">
                                <div className="admin-card__head">
                                    <h2 className="admin-card__title">Formulare</h2>
                                    <Badge bg="secondary">{forms.length + kostuemForms.length}</Badge>
                                </div>
                                <div className="admin-picker">
                                    {forms.map(f => auswahlKnopf(
                                        f.component,
                                        f.title || f.component,
                                        FORM_INFO()[f.component] || f.component,
                                        f.fieldCount,
                                        f.valid,
                                    ))}
                                </div>

                                <div className="admin-card__head mt-2">
                                    <h2 className="admin-card__title">Rund um {cfg.costumeTermPlural}</h2>
                                    <Badge bg="secondary">{kostuemForms.length}</Badge>
                                </div>
                                <div className="admin-picker">
                                    {kostuemForms.map(f => auswahlKnopf(
                                        f.component,
                                        f.bezeichnung || f.component,
                                        'Allgemeine Felder — gelten für jeden Typ',
                                        (f.felder || []).length,
                                        true,
                                    ))}
                                </div>
                            </div>
                        </Col>

                        {/* ── Editor ────────────────────────────────────────────── */}
                        <Col lg={8}>
                            {selected && istKostuemFormular(selected) ? (
                                <div className="admin-card">
                                    <div className="admin-card__head">
                                        <h2 className="admin-card__title">
                                            {kostuemForms.find(f => f.component === selected)?.bezeichnung || selected}
                                        </h2>
                                    </div>
                                    <div className="admin-card__body">
                                        <AdminCostumeForms component={selected} typen={typen} />
                                    </div>
                                </div>
                            ) : !scheme ? (
                                <div className="admin-card">
                                    <div className="admin-card__body text-center py-5">
                                        <FaWpforms size={34} className="mb-3" style={{ opacity: 0.25 }} />
                                        <p className="mb-0 text-secondary">Wähle links ein Formular aus.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="admin-card">
                                    <div className="admin-card__head">
                                        <h2 className="admin-card__title">{scheme.title}</h2>
                                        {geaendert && <Badge bg="warning" text="dark">nicht gespeichert</Badge>}
                                    </div>

                                    <div className="admin-card__body">
                                        <Form.Group className="mb-3">
                                            <Form.Label className="admin-field__label">Überschrift des Formulars</Form.Label>
                                            <Form.Control
                                                value={scheme.title}
                                                onChange={e => setScheme({ ...scheme, title: e.target.value })}
                                            />
                                        </Form.Group>

                                        {scheme.fields.map((f, i) => {
                                            const gesperrt = gesperrtes(f.name);
                                            const istZeit  = ZEIT_TYPEN.includes(f.type);
                                            return (
                                                <div className="admin-field" key={i}>
                                                    <div className="admin-field__pos">{i + 1}</div>

                                                    <div className="admin-field__c-label">
                                                        <label className="admin-field__label">Beschriftung</label>
                                                        <Form.Control size="sm" value={f.label || ''}
                                                            placeholder="z. B. Abfahrt Zunftstube"
                                                            onChange={e => setzeFeld(i, 'label', e.target.value)} />
                                                    </div>

                                                    <div className="admin-field__c-name">
                                                        <label className="admin-field__label">Technischer Name</label>
                                                        <Form.Control size="sm" value={f.name || ''} disabled={gesperrt}
                                                            placeholder="z. B. departureZunftstube"
                                                            onChange={e => setzeFeld(i, 'name', e.target.value)} />
                                                        {gesperrt && (
                                                            <span className="admin-locked"><FaLock /> vom System benötigt</span>
                                                        )}
                                                    </div>

                                                    <div className="admin-field__c-type">
                                                        <label className="admin-field__label">Typ</label>
                                                        <Form.Select size="sm" value={f.type}
                                                            onChange={e => setzeFeld(i, 'type', e.target.value)}>
                                                            {Object.entries(TYPE_LABELS()).map(([wert, text]) => (
                                                                <option key={wert} value={wert}>{text}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </div>

                                                    <div className="admin-field__flags">
                                                        <div className="admin-flag">
                                                            <Form.Check checked={!!f.required}
                                                                onChange={e => setzeFeld(i, 'required', e.target.checked)} />
                                                            <span>Pflicht</span>
                                                        </div>
                                                        {istUmzug && (
                                                            <div className="admin-flag">
                                                                {istZeit ? (
                                                                    <Form.Check checked={!!f.qrDeparture}
                                                                        onChange={e => setzeFeld(i, 'qrDeparture', e.target.checked)} />
                                                                ) : <span style={{ opacity: 0.3 }}>–</span>}
                                                                <span><FaQrcode className="me-1" />Abfahrt</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="admin-field__actions">
                                                        <button className="admin-iconbtn" disabled={i === 0}
                                                            onClick={() => verschiebe(i, -1)} title="nach oben"><FaChevronUp /></button>
                                                        <button className="admin-iconbtn" disabled={i === scheme.fields.length - 1}
                                                            onClick={() => verschiebe(i, 1)} title="nach unten"><FaChevronDown /></button>
                                                        <button className="admin-iconbtn admin-iconbtn--danger" disabled={gesperrt}
                                                            onClick={() => entferne(i)} title="entfernen"><FaTrash /></button>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <Button variant="outline-light" size="sm" className="mt-1"
                                            onClick={() => setScheme({ ...scheme, fields: [...scheme.fields, leeresFeld()] })}>
                                            <FaPlus className="me-1" />Feld hinzufügen
                                        </Button>

                                        <div className="admin-actions">
                                            <Button variant="warning" disabled={!geaendert || saving} onClick={speichere}>
                                                {saving ? <Spinner size="sm" animation="border" /> : <><FaSave className="me-1" />Speichern</>}
                                            </Button>
                                            <Button variant="outline-secondary" disabled={!geaendert}
                                                onClick={() => setScheme(JSON.parse(original))}>
                                                <FaUndo className="me-1" />Verwerfen
                                            </Button>
                                            <span className="ms-auto small text-secondary">
                                                {scheme.fields.length} {scheme.fields.length === 1 ? 'Feld' : 'Felder'}
                                            </span>
                                        </div>

                                        <div className="admin-hint">
                                            Der <b>technische Name</b> ist der Schlüssel, unter dem der Wert gespeichert wird.
                                            Wird er bei einem bestehenden Feld geändert, sind die bisher erfassten Werte nicht
                                            mehr auffindbar — alte Einträge erscheinen leer. Ein gelöschtes Feld verliert
                                            dagegen keine Daten: Legst du es mit demselben Namen wieder an, sind die Werte zurück.
                                            {istUmzug && (
                                                <>
                                                    <br /><br />
                                                    <b>Abfahrt</b> bestimmt, welche Uhrzeiten den QR-Code für die Busfahrt
                                                    auslösen. Er erscheint zwei Stunden vor der <i>frühesten</i> markierten Zeit
                                                    und verschwindet zwei Stunden danach. Der zweite QR-Code für den
                                                    Aufstellungsplatz hängt fest an Beginn und Rückfahrt.
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>
                ) : (
                    /* ── Typen ─────────────────────────────────────────────────── */
                    <Row className="g-3">
                        <Col lg={12}>
                            <div className="admin-card">
                                <div className="admin-card__head">
                                    <h2 className="admin-card__title">Neuen {cfg.costumeTerm}typ anlegen</h2>
                                </div>
                                <div className="admin-card__body">
                                    <Form onSubmit={legeTypAn} className="row g-2">
                                        <div className="col-12 col-md">
                                            <Form.Control
                                                placeholder="Name des Typs, z. B. Hexe"
                                                value={neuerTyp}
                                                onChange={e => setNeuerTyp(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <Button type="submit" variant="warning" disabled={legtAn || !neuerTyp.trim()}>
                                                {legtAn ? <Spinner size="sm" animation="border" /> : <><FaPlus className="me-1" />Anlegen</>}
                                            </Button>
                                        </div>
                                    </Form>
                                    <div className="admin-hint mt-2">
                                        Ein neuer Typ erscheint sofort in der Auswahl des Antrags. Welche Felder
                                        dort zusätzlich abgefragt werden und welche Teile sich bestellen lassen,
                                        legst du unten beim Typ fest.
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {typen.length === 0 ? (
                            <Col lg={12}>
                                <Alert variant="info" className="mb-0">
                                    Noch keine {cfg.costumeTerm}typen angelegt.
                                </Alert>
                            </Col>
                        ) : typen.map(t => (
                            <Col lg={12} key={t.id}>
                                <div className="admin-card">
                                    <div className="admin-card__head">
                                        <h2 className="admin-card__title">{t.name}</h2>
                                        <Button variant="outline-danger" size="sm" onClick={() => loescheTyp(t)}>
                                            <FaTrash className="me-1" />Löschen
                                        </Button>
                                    </div>
                                    <div className="admin-card__body">
                                        <AdminCostumeTypeDefinition typId={t.id} typName={t.name} />
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}
            </div>
        </>
    );
};

export default AdminForms;
